// =============================================================
//  AdminVerificationAnnonceDetailScreen — Vérifier une annonce
//  (approuver / rejeter avec raison) + Modal de refus.
// =============================================================

import React, { useState } from 'react';
import { View, Text, StyleSheet, Modal, TextInput, Alert } from 'react-native';
import { doc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Badge, Divider } from './web-ui/kit';
import { colors, spacing, radius } from './web-ui/theme';

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('fr-FR');
};

const stateVariant = (e) => {
  const v = String(e || '').toLowerCase();
  if (v === 'approved') return 'success';
  if (v === 'rejected') return 'danger';
  return 'warning';
};

export default function AdminVerificationAnnonceDetailScreen({ route, navigation }) {
  const announcement = route?.params?.announcement || {};
  const [loading, setLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [reason, setReason] = useState('');

  const approve = async () => {
    if (!confirm('Approuver cette annonce ?')) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, 'announcements', announcement.id), {
        etat: 'approved',
        updatedAt: serverTimestamp(),
      });
      try {
        await addDoc(collection(db, 'notifications'), {
          userId: announcement?.centerInfo?.centerId,
          title: 'Annonce approuvée',
          message: `Votre annonce "${announcement.titre || announcement.title}" a été approuvée.`,
          type: 'ANNOUNCEMENT_APPROVED',
          read: false,
          createdAt: serverTimestamp(),
        });
      } catch {}
      Alert.alert('Succès', 'L\'annonce a été approuvée');
      navigation.goBack();
    } catch (e) {
      Alert.alert('Erreur', 'Action impossible');
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) {
      Alert.alert('Erreur', 'Indique la raison du refus');
      return;
    }
    setLoading(true);
    try {
      await updateDoc(doc(db, 'announcements', announcement.id), {
        etat: 'rejected',
        rejectionReason: reason,
        updatedAt: serverTimestamp(),
      });
      try {
        await addDoc(collection(db, 'notifications'), {
          userId: announcement?.centerInfo?.centerId,
          title: 'Annonce refusée',
          message: `Votre annonce "${announcement.titre || announcement.title}" a été refusée.\nRaison : ${reason}`,
          type: 'ANNOUNCEMENT_REJECTED',
          read: false,
          createdAt: serverTimestamp(),
        });
      } catch {}
      setRejectModal(false);
      Alert.alert('Succès', 'L\'annonce a été refusée');
      navigation.goBack();
    } catch (e) {
      Alert.alert('Erreur', 'Action impossible');
    } finally {
      setLoading(false);
    }
  };

  const title = announcement.titre || announcement.title || 'Annonce';
  const isPending = announcement.etat === 'pending' || !announcement.etat;

  return (
    <AdminLayout title={title} subtitle="Vérification d'annonce">
      <View style={styles.actions}>
        <Button variant="secondary" icon="arrow-left" onPress={() => navigation.goBack()}>Retour</Button>
        {isPending && (
          <>
            <Button variant="primary" icon="check" loading={loading} onPress={approve}>
              Approuver
            </Button>
            <Button variant="danger" icon="close" onPress={() => setRejectModal(true)}>
              Refuser
            </Button>
          </>
        )}
      </View>

      <Card>
        <View style={{ flexDirection: 'row', gap: 6, marginBottom: spacing.md, flexWrap: 'wrap' }}>
          <Badge variant={stateVariant(announcement.etat)}>
            {announcement.etat === 'approved'
              ? 'Approuvée'
              : announcement.etat === 'rejected'
              ? 'Rejetée'
              : 'En attente'}
          </Badge>
          {!!announcement.type && <Badge variant="primary">{announcement.type}</Badge>}
        </View>

        <Text style={styles.title}>{title}</Text>
        {!!announcement.subtitle && <Text style={styles.subtitle}>{announcement.subtitle}</Text>}

        <Divider />

        {!!announcement.description && (
          <>
            <Text style={styles.section}>Description</Text>
            <Text style={styles.body}>{announcement.description}</Text>
            <Divider />
          </>
        )}

        <View style={styles.grid}>
          <Info label="Auteur" value={announcement.centerName || announcement.centerInfo?.centerName} />
          <Info label="Date" value={fmtDate(announcement.createdAt)} />
          {!!announcement.expiresAt && <Info label="Expire le" value={fmtDate(announcement.expiresAt)} />}
        </View>

        {!!announcement.rejectionReason && (
          <>
            <Divider />
            <Text style={[styles.section, { color: colors.danger }]}>Raison du refus</Text>
            <Text style={styles.body}>{announcement.rejectionReason}</Text>
          </>
        )}
      </Card>

      <Modal visible={rejectModal} transparent animationType="fade" onRequestClose={() => setRejectModal(false)}>
        <View style={styles.modalBg}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Refuser l'annonce</Text>
            <Text style={styles.modalSub}>Indique pourquoi cette annonce est refusée.</Text>
            <TextInput
              value={reason}
              onChangeText={setReason}
              placeholder="Raison du refus..."
              placeholderTextColor={colors.textMuted}
              style={styles.modalInput}
              multiline
            />
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onPress={() => setRejectModal(false)}>Annuler</Button>
              <Button variant="danger" icon="close" loading={loading} onPress={reject}>Refuser</Button>
            </View>
          </View>
        </View>
      </Modal>
    </AdminLayout>
  );
}

function Info({ label, value }) {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{value || '—'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginBottom: spacing.md, flexWrap: 'wrap' },
  title: { fontSize: 22, fontWeight: '700', color: colors.text, letterSpacing: -0.3 },
  subtitle: { fontSize: 14, color: colors.textSecondary, marginTop: 4 },
  section: { fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.sm },
  body: { fontSize: 14, color: colors.textSecondary, lineHeight: 21 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  infoItem: {
    flex: 1,
    minWidth: 200,
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
  },
  infoLabel: { fontSize: 11, color: colors.textMuted, fontWeight: '600', textTransform: 'uppercase' },
  infoValue: { fontSize: 14, color: colors.text, fontWeight: '600', marginTop: 4 },

  modalBg: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modalCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: spacing.lg,
    width: '100%',
    maxWidth: 480,
  },
  modalTitle: { fontSize: 17, fontWeight: '700', color: colors.text },
  modalSub: { fontSize: 13, color: colors.textSecondary, marginTop: 4 },
  modalInput: {
    marginTop: 12,
    minHeight: 90,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 12,
    color: colors.text,
    outlineStyle: 'none',
    textAlignVertical: 'top',
  },
});
